import React, { Fragment, useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import Header from '../../components/Header';
import Load from '../../components/Load';
import SelectPartnerModal, { Partner } from '../../components/SelectPartnerModal';
import InscricaoStatusChip from '../../components/admin/InscricaoStatusChip';
import {
  INSCRICAO_POR_ID,
  InscricaoPorIdData,
  InscricaoPorIdVars,
  trocarParceiro,
  consentirCompartilharInformacoes,
} from '../../services/inscricaoTorneioService';
import { StatusInscricao } from '../../types/inscricao';
import { StatusCategoriaTorneio } from '../../types/categoriaTorneio';
import { useAuth } from '../../context/AuthContext';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import Tooltip from '@mui/material/Tooltip';

const InscricaoDetailPage: React.FC = () => {
  const { inscricaoId } = useParams<{ inscricaoId: string }>();
  const navigate = useNavigate();
  const auth = useAuth();
  const user = (auth?.user as any) || (typeof window !== 'undefined' ? JSON.parse(localStorage.getItem('user') || 'null') : null);
  const atletaLogadoId = user?.atleta?.id ?? '';

  const { data, loading, error, refetch } = useQuery<InscricaoPorIdData, InscricaoPorIdVars>(
    INSCRICAO_POR_ID,
    {
      variables: { id: inscricaoId || '' },
      skip: !inscricaoId,
      fetchPolicy: 'no-cache',
    },
  );

  const inscricao = data?.inscricaoTorneioPorId;

  const [selectPartnerOpen, setSelectPartnerOpen] = useState(false);
  const [swapLoading, setSwapLoading] = useState(false);
  const [swapError, setSwapError] = useState<string | null>(null);
  const [swapSuccess, setSwapSuccess] = useState<string | null>(null);

  const [consentDialogOpen, setConsentDialogOpen] = useState(false);
  const [consentChecked, setConsentChecked] = useState(false);
  const [consentLoading, setConsentLoading] = useState(false);
  const [consentSuccess, setConsentSuccess] = useState<string | null>(null);
  const [consentError, setConsentError] = useState<string | null>(null);

  useEffect(() => {
    if (
      inscricao &&
      !inscricao.compartilharInformacoes &&
      inscricao.status !== StatusInscricao.CANCELADA
    ) {
      setConsentDialogOpen(true);
    }
  }, [inscricao]);

  const handleConsentir = async () => {
    if (!inscricaoId) return;
    setConsentLoading(true);
    setConsentError(null);
    try {
      await consentirCompartilharInformacoes(inscricaoId);
      setConsentDialogOpen(false);
      setConsentChecked(false);
      setConsentSuccess('Compartilhamento de informações autorizado com sucesso.');
      await refetch();
    } catch (err: any) {
      console.error(err);
      setConsentError(err.message || 'Erro ao autorizar compartilhamento.');
    } finally {
      setConsentLoading(false);
    }
  };

  const getSwapDisabledReason = (): string | null => {
    if (!inscricao) return null;
    const { categoriaTorneio, status } = inscricao;
    const torneioFlag = categoriaTorneio?.torneio?.atletaTrocaParceiro;
    const numAtletas = categoriaTorneio?.numeroAtletas ?? 1;
    const catStatus = categoriaTorneio?.status;
    const validCatStatuses = [
      StatusCategoriaTorneio.INSCRICAO_ABERTA,
      StatusCategoriaTorneio.AGENDADA,
    ];
    if (torneioFlag !== true)
      return 'O torneio não permite troca de parceiro';
    if (numAtletas < 2)
      return 'Categoria individual não permite troca de parceiro';
    if (status === StatusInscricao.CANCELADA)
      return 'Não é possível trocar parceiro de uma inscrição cancelada';
    if (status === StatusInscricao.FILA_ESPERA)
      return 'Não é possível trocar parceiro de uma inscrição em fila de espera';
    if (!validCatStatuses.includes(catStatus as StatusCategoriaTorneio))
      return 'As inscrições desta categoria não estão abertas';
    return null;
  };

  const handleSelectPartner = async (partner: Partner) => {
    if (!inscricaoId) return;

    const atletaSaiId = inscricao?.equipe?.atletas?.find(
      (a) => a.id !== atletaLogadoId,
    )?.id;

    if (!atletaSaiId) {
      setSwapError('Não foi possível identificar o parceiro a ser substituído.');
      return;
    }

    setSelectPartnerOpen(false);
    setSwapLoading(true);
    setSwapError(null);
    setSwapSuccess(null);

    try {
      await trocarParceiro(inscricaoId, atletaSaiId, partner.id);
      setSwapSuccess(`Parceiro trocado com sucesso para ${partner.nome}.`);
      await refetch();
    } catch (err: any) {
      console.error(err);
      setSwapError(err.message || 'Erro ao trocar parceiro.');
    } finally {
      setSwapLoading(false);
    }
  };

  return (
    <Fragment>
      <Header />
      <div className="main-content right-chat-active">
        <div className="middle-sidebar-bottom">
          <div className="middle-sidebar-left pe-0">
            <div className="row">
              <div className="col-xl-12 col-xxl-12 col-lg-12">
                <button
                  className="btn btn-link text-primary font-xsss fw-600 p-0 mb-3"
                  onClick={() => navigate(-1)}
                >
                  ← Voltar
                </button>

                {loading && <Load />}
                {error && (
                  <div className="alert alert-danger" role="alert">
                    Erro ao carregar inscrição.
                  </div>
                )}

                {!loading && !error && !inscricao && (
                  <div className="alert alert-warning" role="alert">
                    Inscrição não encontrada.
                  </div>
                )}

                {inscricao && (
                  <div className="card w-100 shadow-xss rounded-xxl border-0 p-4">
                    <h4 className="fw-700 text-grey-900 mb-3">Detalhes da Inscrição</h4>

                    {swapSuccess && (
                      <div className="alert alert-success py-2">{swapSuccess}</div>
                    )}
                    {swapError && (
                      <div className="alert alert-danger py-2">{swapError}</div>
                    )}
                    {consentSuccess && (
                      <div className="alert alert-success py-2">{consentSuccess}</div>
                    )}
                    {consentError && (
                      <div className="alert alert-danger py-2">{consentError}</div>
                    )}

                    <div className="mb-3">
                      <div className="font-xsss text-grey-500 mb-1">Torneio</div>
                      <div className="fw-600 text-grey-900">
                        {inscricao.categoriaTorneio?.torneio?.nome || '—'}
                      </div>
                    </div>

                    <div className="mb-3">
                      <div className="font-xsss text-grey-500 mb-1">Categoria</div>
                      <div className="fw-600 text-grey-900">
                        {inscricao.categoriaTorneio?.nome || '—'}
                      </div>
                    </div>

                    <div className="mb-3">
                      <div className="font-xsss text-grey-500 mb-1">Status da Inscrição</div>
                      <InscricaoStatusChip status={inscricao.status} />
                    </div>

                    {inscricao.pagamentoInscricao && (
                      <div className="mb-3">
                        <div className="font-xsss text-grey-500 mb-1">Pagamento</div>
                        <div className="d-flex align-items-center gap-2">
                          <InscricaoStatusChip status={inscricao.pagamentoInscricao.status} />
                          {inscricao.pagamentoInscricao.valor != null && (
                            <span className="fw-600 text-grey-900">
                              R$ {inscricao.pagamentoInscricao.valor.toFixed(2)}
                            </span>
                          )}
                        </div>
                      </div>
                    )}

                    <div className="mb-3">
                      <div className="font-xsss text-grey-500 mb-1">Equipe</div>
                      <div className="fw-600 text-grey-900 mb-2">
                        {inscricao.equipe?.nome || '—'}
                      </div>
                      {inscricao.equipe?.atletas?.map((atleta) => (
                        <div key={atleta.id} className="d-flex align-items-center mb-2">
                          <img
                            src={atleta.usuario?.imagemUrl || '/assets/images/user.png'}
                            alt={atleta.usuario?.nome ?? 'Atleta'}
                            className="rounded-circle me-2"
                            style={{ width: 40, height: 40, objectFit: 'cover' }}
                          />
                          <span className="fw-600 text-grey-900">{atleta.usuario?.nome ?? 'Atleta'}</span>
                        </div>
                      ))}
                    </div>

                    {!inscricao.compartilharInformacoes && inscricao.status !== StatusInscricao.CANCELADA && (
                      <div className="alert alert-warning py-2 mb-3">
                        Você ainda não autorizou o compartilhamento de informações de contato com a organização.{' '}
                        <button
                          className="btn btn-link p-0 fw-600"
                          onClick={() => setConsentDialogOpen(true)}
                        >
                          Autorizar agora
                        </button>
                      </div>
                    )}

                    {(() => {
                      const disabledReason = getSwapDisabledReason();
                      return disabledReason ? (
                        <Tooltip title={disabledReason} placement="top">
                          <span>
                            <button
                              className="btn btn-outline-primary mt-2"
                              disabled
                            >
                              Trocar Parceiro
                            </button>
                          </span>
                        </Tooltip>
                      ) : (
                        <button
                          className="btn btn-outline-primary mt-2"
                          onClick={() => setSelectPartnerOpen(true)}
                          disabled={swapLoading}
                        >
                          {swapLoading ? 'Trocando...' : 'Trocar Parceiro'}
                        </button>
                      );
                    })()}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      <SelectPartnerModal
        open={selectPartnerOpen}
        onClose={() => setSelectPartnerOpen(false)}
        recentPartners={[]}
        onSelect={handleSelectPartner}
      />

      <Dialog
        open={consentDialogOpen}
        onClose={() => setConsentDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Compartilhamento de informações</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Para que a organização do torneio possa entrar em contato com você,
            é necessário autorizar o compartilhamento das suas informações de
            <strong> e-mail</strong> e <strong>telefone</strong>.
          </Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={consentChecked}
                onChange={(e) => setConsentChecked(e.target.checked)}
                disabled={consentLoading}
              />
            }
            label="Autorizo o compartilhamento das minhas informações de e-mail e telefone com a organização do torneio para fins de comunicação e gestão do evento."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConsentDialogOpen(false)} disabled={consentLoading}>
            Cancelar
          </Button>
          <Button
            onClick={handleConsentir}
            variant="contained"
            disabled={!consentChecked || consentLoading}
          >
            {consentLoading ? 'Autorizando...' : 'Autorizar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Fragment>
  );
};

export default InscricaoDetailPage;
